import gsap from 'gsap';
import type { Beat, DeckDoc, SlideDoc } from '../types/slide-doc';

type Timeline = ReturnType<typeof gsap.timeline>;

// Master synchronisation controller for a lesson playback.
//
// Drives everything from a single time source:
//   - if narration audio is present → audio.currentTime is the master clock
//   - if absent (preview / unauthored decks) → wall-clock driven by RAF
//
// SlideView registers per-slide GSAP timelines with this controller.
// The controller seeks the active slide's timeline on every tick.
//
// Owns:
//   - active slide computation from cumulative durations
//   - beat pause/resume state machine
//   - speed multiplier
//   - subscriber notifications
//
// Does NOT own:
//   - DOM (SlideView mounts shapes)
//   - audio scheduling for stingers (existing audioRuntime keeps that, scheduled
//     against AudioContext.currentTime + lookahead — playhead just provides
//     "is the lesson paused?" so cues hold during beat pauses)

export interface SlideTiming {
  slide: SlideDoc;
  startMs: number;
  durationMs: number;
  endMs: number;
}

export interface PlayheadState {
  currentMs: number;
  activeSlideId: string | null;
  activeChapterId: string | null;
  isPaused: boolean;
  isPlaying: boolean;
  pausedAtBeat: Beat | null;
  speed: number;
  durationMs: number;
}

type Listener = (state: PlayheadState) => void;

const DEFAULT_SLIDE_DURATION_MS = 6000;

export class LessonPlayhead {
  private deck: DeckDoc;
  private narration: HTMLAudioElement | null;
  private timings: SlideTiming[];
  private slideById: Map<string, SlideTiming>;
  private timelinesBySlide: Map<string, Timeline> = new Map();
  private listeners: Set<Listener> = new Set();

  private rafId: number | null = null;
  private lastTickMs: number = 0;

  // Virtual clock used when no narration audio is loaded
  private virtualMs: number = 0;
  private virtualPlaying: boolean = false;

  private speed: number = 1;
  private pausedAtBeat: Beat | null = null;
  // Beats already triggered so we don't re-pause on the same beat after a seek
  private firedBeats: Set<string> = new Set();
  private activeSlideId: string | null = null;

  constructor(deck: DeckDoc, narration?: HTMLAudioElement | null) {
    this.deck = deck;
    this.narration = narration ?? null;
    this.timings = computeTimings(deck);
    this.slideById = new Map(this.timings.map((t) => [t.slide.id, t]));

    if (this.narration) {
      this.narration.addEventListener('seeked', this.onNarrationSeeked);
      this.narration.addEventListener('ended', this.onNarrationEnded);
      this.narration.preservesPitch = true;
    }

    this.startTickLoop();
  }

  // ─── Public API ──────────────────────────────────────────────────────

  play() {
    if (this.pausedAtBeat) return; // beat pause must resume via tapContinue()
    if (this.narration) {
      void this.narration.play();
    } else {
      this.virtualPlaying = true;
      this.lastTickMs = performance.now();
    }
    this.notify();
  }

  pause() {
    if (this.narration) {
      this.narration.pause();
    } else {
      this.virtualPlaying = false;
    }
    this.notify();
  }

  seek(ms: number) {
    const clamped = Math.max(0, Math.min(ms, this.totalDurationMs()));
    if (this.narration) {
      this.narration.currentTime = clamped / 1000;
    } else {
      this.virtualMs = clamped;
    }
    // Reset beat memory in the future; keep past-fired beats fired
    this.firedBeats = new Set(
      [...this.firedBeats].filter((bid) => {
        const [slideId, beatId] = bid.split('::');
        const slide = this.slideById.get(slideId)?.slide;
        const beat = slide?.beats?.find((b) => b.id === beatId);
        if (!slide || !beat) return false;
        const beatGlobalMs = (this.slideById.get(slideId)?.startMs ?? 0) + beat.atMs;
        return beatGlobalMs <= clamped;
      })
    );
    this.pausedAtBeat = null;
    this.tick(); // immediate visual update
  }

  setSpeed(x: number) {
    this.speed = Math.max(0.25, Math.min(x, 3));
    if (this.narration) this.narration.playbackRate = this.speed;
    // GSAP timelines: we drive them via seek() so speed naturally applies
    this.notify();
  }

  tapContinue() {
    if (!this.pausedAtBeat) return;
    this.pausedAtBeat = null;
    this.play();
  }

  registerSlideTimeline(slideId: string, timeline: Timeline) {
    this.timelinesBySlide.set(slideId, timeline);
    // Position it immediately to where the playhead is right now
    const t = this.slideById.get(slideId);
    if (t) {
      const rel = Math.max(0, this.getCurrentMs() - t.startMs) / 1000;
      timeline.seek(rel, false);
    }
  }

  unregisterSlideTimeline(slideId: string) {
    const tl = this.timelinesBySlide.get(slideId);
    if (tl) tl.kill();
    this.timelinesBySlide.delete(slideId);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.getState());
    return () => this.listeners.delete(listener);
  }

  getState(): PlayheadState {
    const cur = this.getCurrentMs();
    const slideId = this.activeSlideId;
    const chapterId = this.findChapterFor(slideId);
    return {
      currentMs: cur,
      activeSlideId: slideId,
      activeChapterId: chapterId,
      isPaused: this.pausedAtBeat !== null || !this.isPlayingClock(),
      isPlaying: this.isPlayingClock(),
      pausedAtBeat: this.pausedAtBeat,
      speed: this.speed,
      durationMs: this.totalDurationMs(),
    };
  }

  getTimings(): SlideTiming[] {
    return this.timings;
  }

  destroy() {
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    if (this.narration) {
      this.narration.removeEventListener('seeked', this.onNarrationSeeked);
      this.narration.removeEventListener('ended', this.onNarrationEnded);
    }
    for (const tl of this.timelinesBySlide.values()) tl.kill();
    this.timelinesBySlide.clear();
    this.listeners.clear();
  }

  // ─── Internal ────────────────────────────────────────────────────────

  private totalDurationMs(): number {
    return this.timings.length === 0 ? 0 : this.timings[this.timings.length - 1].endMs;
  }

  private getCurrentMs(): number {
    if (this.narration) return this.narration.currentTime * 1000;
    return this.virtualMs;
  }

  private isPlayingClock(): boolean {
    if (this.pausedAtBeat) return false;
    if (this.narration) return !this.narration.paused;
    return this.virtualPlaying;
  }

  private startTickLoop() {
    const loop = (now: number) => {
      this.tick(now);
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  private tick = (now: number = performance.now()) => {
    // Advance virtual clock if no narration
    if (!this.narration && this.virtualPlaying && !this.pausedAtBeat) {
      const dt = (now - this.lastTickMs) * this.speed;
      this.virtualMs = Math.min(this.virtualMs + dt, this.totalDurationMs());
      if (this.virtualMs >= this.totalDurationMs()) this.virtualPlaying = false;
    }
    this.lastTickMs = now;

    const cur = this.getCurrentMs();

    // Determine active slide
    const newActive = this.findSlideAtMs(cur);
    if (newActive?.slide.id !== this.activeSlideId) {
      this.activeSlideId = newActive?.slide.id ?? null;
    }

    // Drive active slide's timeline
    if (newActive) {
      const tl = this.timelinesBySlide.get(newActive.slide.id);
      if (tl) {
        const rel = Math.max(0, cur - newActive.startMs) / 1000;
        tl.seek(rel, false);
      }
      this.checkBeats(newActive, cur);
    }

    this.notify();
  };

  private findSlideAtMs(ms: number): SlideTiming | undefined {
    if (this.timings.length === 0) return undefined;
    if (ms <= 0) return this.timings[0];
    // Linear scan — deck size is bounded
    for (const t of this.timings) {
      if (ms >= t.startMs && ms < t.endMs) return t;
    }
    return this.timings[this.timings.length - 1];
  }

  private checkBeats(t: SlideTiming, globalMs: number) {
    const beats = t.slide.beats ?? [];
    if (beats.length === 0) return;
    const slideMs = globalMs - t.startMs;
    for (const beat of beats) {
      const key = `${t.slide.id}::${beat.id}`;
      if (this.firedBeats.has(key)) continue;
      if (slideMs < beat.atMs) continue;
      this.firedBeats.add(key);
      const mode = beat.resumesOn ?? 'tap';
      if (mode === 'tap') {
        this.pausedAtBeat = beat;
        if (this.narration) this.narration.pause();
        else this.virtualPlaying = false;
      } else if (mode === 'auto+Nms') {
        const ms = beat.autoResumeMs ?? 1500;
        if (this.narration) {
          this.narration.pause();
          window.setTimeout(() => {
            void this.narration?.play();
          }, ms);
        } else {
          this.virtualPlaying = false;
          window.setTimeout(() => {
            this.virtualPlaying = true;
            this.lastTickMs = performance.now();
          }, ms);
        }
      }
    }
  }

  private findChapterFor(slideId: string | null): string | null {
    if (!slideId || !this.deck.chapters) return null;
    // The chapter whose slideId is the latest one ≤ slideId in deck order
    const order = this.deck.slides.map((s) => s.id);
    const targetIdx = order.indexOf(slideId);
    if (targetIdx < 0) return null;
    let bestChapter: string | null = null;
    let bestIdx = -1;
    for (const ch of this.deck.chapters) {
      const idx = order.indexOf(ch.slideId);
      if (idx >= 0 && idx <= targetIdx && idx > bestIdx) {
        bestChapter = ch.id;
        bestIdx = idx;
      }
    }
    return bestChapter;
  }

  private onNarrationSeeked = () => {
    // Replay any past-fired beat memory for the new position
    const cur = this.getCurrentMs();
    this.firedBeats = new Set(
      [...this.firedBeats].filter((bid) => {
        const [slideId, beatId] = bid.split('::');
        const slide = this.slideById.get(slideId)?.slide;
        const beat = slide?.beats?.find((b) => b.id === beatId);
        if (!slide || !beat) return false;
        const beatGlobalMs = (this.slideById.get(slideId)?.startMs ?? 0) + beat.atMs;
        return beatGlobalMs <= cur;
      })
    );
    this.pausedAtBeat = null;
  };

  private onNarrationEnded = () => {
    this.notify();
  };

  private notify() {
    const state = this.getState();
    for (const l of this.listeners) l(state);
  }
}

// Compute cumulative start/end ms per slide. If a slide has no explicit
// durationMs, derive from the max(atMs+durationMs) of its animations,
// falling back to DEFAULT_SLIDE_DURATION_MS.
export function computeTimings(deck: DeckDoc): SlideTiming[] {
  const out: SlideTiming[] = [];
  let cursor = 0;
  for (const slide of deck.slides) {
    const duration = explicitOrDerivedDuration(slide);
    out.push({
      slide,
      startMs: cursor,
      durationMs: duration,
      endMs: cursor + duration,
    });
    cursor += duration;
  }
  return out;
}

function explicitOrDerivedDuration(slide: SlideDoc): number {
  if (slide.durationMs && slide.durationMs > 0) return slide.durationMs;
  const anims = slide.animations ?? [];
  let max = 0;
  for (const a of anims) {
    const end = a.atMs + a.durationMs;
    if (end > max) max = end;
  }
  // Add 500ms settle tail after the last animation if derived
  return max > 0 ? max + 500 : DEFAULT_SLIDE_DURATION_MS;
}
